import { useEffect, useState, type FormEvent } from 'react'
import { Button, ErrorBox, Loading } from '../components/ui'
import { useAuth } from '../lib/auth'
import { errorMessage } from '../lib/errors'
import { APP_NAME, supabase } from '../lib/supabase'

/**
 * Second factor (TOTP) for staff. The database refuses staff functions unless the JWT is aal2,
 * so this screen is mandatory, not cosmetic.
 */
export function MfaPage() {
  const { refreshAal, signOut } = useAuth()
  const [factorId, setFactorId] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const { data, error: e } = await supabase.auth.mfa.listFactors()
        if (e) throw e
        const verified = data.totp.find((f) => f.status === 'verified')
        if (verified) {
          setFactorId(verified.id)
        } else {
          // Remove half-finished enrolments, then start a fresh one.
          for (const f of data.all.filter((x) => x.status !== 'verified')) await supabase.auth.mfa.unenroll({ factorId: f.id })
          const { data: en, error: e2 } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: `${APP_NAME} ${new Date().toISOString().slice(0, 10)}` })
          if (e2) throw e2
          setFactorId(en.id)
          setQr(en.totp.qr_code)
          setSecret(en.totp.secret)
        }
      } catch (err) {
        setError(errorMessage(err))
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  async function verify(e: FormEvent) {
    e.preventDefault()
    if (!factorId) return
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() })
      if (err) throw new Error('Código incorrecto o vencido. Use el código actual de su aplicación.')
      await refreshAal()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="center-page login-bg">
      <form className="login-card" onSubmit={verify}>
        <h1>Verificación en dos pasos</h1>
        {loading ? <Loading /> : (
          <>
            {qr ? (
              <>
                <p className="muted">
                  Escanee este código con una app de autenticación (Google Authenticator, Microsoft Authenticator, Aegis…).
                  Es obligatorio para administradores y soporte.
                </p>
                <img src={qr} alt="Código QR para la app de autenticación" width={200} height={200} style={{ alignSelf: 'center', background: '#fff' }} />
                {secret ? <small className="muted">Clave manual: <code>{secret}</code></small> : null}
              </>
            ) : (
              <p className="muted">Escriba el código de 6 dígitos de su aplicación de autenticación.</p>
            )}
            <label className="field">
              <span>Código</span>
              <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
            </label>
            {error ? <ErrorBox message={error} /> : null}
            <Button type="submit" busy={busy} disabled={code.length !== 6}>Verificar</Button>
          </>
        )}
        <button type="button" className="link" onClick={() => void signOut()}>Cerrar sesión</button>
      </form>
    </div>
  )
}
